/* =======================================================================
   Knowledge Base — reader feedback. Azure item 416.

   Three places ask for it, and all three send the same way:
     - the Knowledge Base home page ("Can't find what you need?")
     - under every article ("Was this article helpful?" — Yes/No opens a box)
     - a search that found nothing, or found only weak matches
   Written feedback is posted to /api/kb/feedback, which emails it to the
   Makani team with the reader as reply-to. A Yes/No with nothing written is
   not sent — only what someone actually wrote reaches the inbox.

   Everything here is HTML strings plus one delegated click handler, the same
   as the rest of the article markup, so it works in the page and the drawer
   alike. Both call handleFeedbackClick() first in their own click handler.
   ======================================================================= */
/* The API client is loaded on Send, not here: render.js imports the form, and
   render.js is loaded by the unit tests, which must not pull in the app's
   store and HTTP setup to build an HTML string. */
const kbService = () => import('src/Services/API/kb.service').then(m => m.default);

const MAX = 4000;
const escAttr = s => String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/* The form. `context` says which of the three it is; `search` rides along on
   a no-results form so the email can say what was searched for. */
export const FEEDBACK_FORM = (context, opts = {}) =>
  '<div class="fb" data-fb="' + context + '"' + (opts.search ? ' data-fb-search="' + escAttr(opts.search) + '"' : '') + '>' +
    (opts.label ? '<label class="fb-l">' + opts.label + '</label>' : '') +
    '<textarea class="fb-t" maxlength="' + MAX + '" rows="3" placeholder="' + escAttr(opts.placeholder ||
      'What were you looking for, or what could be better?') + '"></textarea>' +
    '<div class="fb-row">' +
      '<span class="fb-note">Goes straight to the Makani team, who may reply by email.</span>' +
      (opts.skip ? '<button class="fb-skip" type="button" data-fb-skip="1">No thanks</button>' : '') +
      '<button class="fb-send" type="button" data-fb-send="1">Send feedback</button>' +
    '</div>' +
    '<div class="fb-msg" role="status" aria-live="polite"></div>' +
  '</div>';

const THANKS = text => '<div class="thanks"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">' +
  '<path d="M4 12l5 5L20 6"/></svg><span>' + text + '</span></div>';

/* Yes/No under an article: say thanks, and offer the box. The answer is kept
   on the box so Send can include it. */
function answerHelpful(box, answer){
  box.dataset.fbHelpful = answer;
  box.classList.add('asked');
  box.innerHTML = FEEDBACK_FORM('article', {
    skip: true,
    label: answer === 'yes'
      ? 'Glad it helped. Anything we could make clearer? <span class="fb-opt">(optional)</span>'
      : 'Sorry it didn’t help. What were you trying to do? <span class="fb-opt">(optional)</span>',
    placeholder: answer === 'yes' ? 'Anything missing or unclear…' : 'What you were trying to do, or what was wrong…'
  });
  const t = box.querySelector('textarea');
  if (t) t.focus();
}

async function send(form, ctx){
  const t = form.querySelector('textarea');
  const msg = form.querySelector('.fb-msg');
  const btn = form.querySelector('[data-fb-send]');
  const message = ((t && t.value) || '').trim();
  if (!message){
    msg.className = 'fb-msg err';
    msg.textContent = 'Write something first — even a line helps.';
    if (t) t.focus();
    return;
  }
  const context = form.dataset.fb;
  const box = form.closest('#helpful');
  const a = context === 'article' ? ctx.article : null;
  btn.disabled = true;
  btn.textContent = 'Sending…';
  msg.className = 'fb-msg';
  msg.textContent = '';
  try {
    await (await kbService()).sendFeedback({
      context,
      message,
      helpful: box ? box.dataset.fbHelpful || null : null,
      articleSlug: a ? a.slug : undefined,
      articleTitle: a ? a.title : undefined,
      articleRef: a ? a.ref : undefined,
      search: form.dataset.fbSearch || undefined,
      page: window.location.pathname
    });
    form.outerHTML = THANKS('Thanks — that’s gone to the Makani team.');
  } catch (err){
    const status = err && err.response && err.response.status;
    btn.disabled = false;
    btn.textContent = 'Send feedback';
    msg.className = 'fb-msg err';
    msg.textContent = status === 429
      ? 'That’s a lot of feedback in a short time — please try again in a few minutes.'
      : 'It didn’t send. Your message is still here — try again in a moment.';
  }
}

/* The delegated handler. Returns true when the click was ours. `ctx.article`
   is the article on screen, if any. */
export function handleFeedbackClick(e, ctx = {}){
  const t = e.target;
  if (!t.closest) return false;
  const help = t.closest('[data-help]');
  if (help){
    e.preventDefault();
    const box = help.closest('#helpful');
    if (box) answerHelpful(box, help.dataset.help === 'yes' ? 'yes' : 'no');
    return true;
  }
  if (t.closest('[data-fb-skip]')){
    e.preventDefault();
    const box = t.closest('#helpful');
    if (box) box.innerHTML = THANKS('Thanks — noted.');
    return true;
  }
  const sendBtn = t.closest('[data-fb-send]');
  if (sendBtn){
    e.preventDefault();
    const form = sendBtn.closest('[data-fb]');
    if (form && !sendBtn.disabled) send(form, ctx);
    return true;
  }
  return false;
}
